import { z } from "zod";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { Form, useFormHook } from "@/components/FormComps";
import { useState } from "react";
import StepIndicator from "./StepIndicator";
import Step1 from "./Step1";
import Step2 from "./Step2";
import { En, Fa } from "../ui/multilang";
import Space from "../ui/Space";
import { useRouter } from "next/router";
import { sendGTMEvent } from "@next/third-parties/google";

type FinalData = {
  email: string;
  password: string;
  firstname: string;
  lastname: string;
  phoneCountryCode: string;
  phone: string;
};

const stepSchemas = [
  z.object({
    email: z
      .string()
      .min(1, "Please enter user's Email")
      .min(5, "Email must be 5 characters or longer")
      .email({ message: "Please enter a valid email." }),
    password: z.string().min(8, "Password must be 8 characters or longer"),
  }),
  z.object({
    firstname: z.string().min(1, "Please enter user's first name"),
    lastname: z.string().min(1, "Please enter user's last name"),
    phoneCountryCode: z.string().min(1, "This field is required!"),
    phone: z.string().regex(/^(\s*|\d+)$/, "must contain only digits"),
  }),
];

export default function SignupWizard() {
  const { locale } = useRouter();
  const supabase = useSupabaseClient();
  const [formData, setFormData] = useState<Partial<FinalData>>({});
  const [signedUp, setSignedUp] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);

  const onSubmit = async () => {
    // Validate the last step
    const isValid = await form.trigger();
    if (!isValid) return;

    sendGTMEvent({ event: "signup-pdp-modal-button" });

    // Combine final step data
    const finalData = { ...formData, ...form.getValues() } as FinalData;
    const [dialCode] = finalData.phoneCountryCode.split("-");
    const { data: user, error: signUpError } = await supabase.auth.signUp({
      email: finalData.email,
      password: finalData.password,
      options: {
        emailRedirectTo: window.location.href,
        data: {
          firstname: finalData.firstname,
          lastname: finalData.lastname,
          phoneCountryCode: dialCode,
          phone: finalData.phone,
          role: "payer",
        },
      },
    });
    if (signUpError) {
      throw signUpError;
    }

    if (user.user!.identities && user.user!.identities.length === 0) {
      throw new Error("A user with this email address already exists.");
    }

    setSignedUp(true);
  };

  const handleGoogleSignIn = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.href,
      },
    });

    if (error) {
      console.error("Error during sign-in:", error.message);
    } else {
      console.log("Sign-in successful:", data);
    }
  };

  const form = useFormHook({
    schema: stepSchemas[currentStep - 1],
    onSubmit,
    defaultValues: formData,
  });

  if (signedUp) {
    return (
      <>
        <En>
          <p>You are signed up. 😊</p>
        </En>
        <Fa>
          <p>حساب کاربری شما ایجاد شد.</p>
        </Fa>
      </>
    );
  }

  const handleNextStep = async () => {
    const isValid = await form.trigger(); // Validate all visible fields
    if (!isValid) return; // Stay on the current step if validation fails

    const currentData = form.getValues();
    setFormData((prev) => ({ ...prev, ...currentData }));

    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  return (
    <div className="space-y-2 ">
      <div className="">
        <Form form={form} onSubmit={onSubmit} className="" submitText="Sign Up" hasSubmitButton={false}>
          {currentStep === 1 && <Step1 handleNextStep={handleNextStep} />}
          {currentStep === 2 && <Step2 handlePrevStep={handlePrevStep} />}
        </Form>
        <Space size="h-4" />
        <StepIndicator currentStep={currentStep} totalSteps={2} />
        <div className="flex items-center my-4">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="mx-2 text-gray-500">{locale === "fa" ? "یا" : "or"}</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        <button
          onClick={handleGoogleSignIn}
          className="mt-4 w-full flex items-center justify-center border border-gray-500 rounded-lg p-2 bg-white text-gray-900 hover:shadow-md hover:bg-gray-100 focus:outline-none focus:ring focus:ring-gray-300 disabled:bg-gray-100 disabled:opacity-50"
        >
          <div className="flex items-center">
            <div className={`flex-shrink-0 ${locale === "fa" ? "ml-3" : "mr-3"}`}>
              <svg
                className="h-5 w-5"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
                xmlnsXlink="http://www.w3.org/1999/xlink"
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                ></path>
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                ></path>
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                ></path>
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                ></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="font-medium">{locale === "fa" ? "ثبت‌نام با گوگل" : "Sign up with Google"}</span>
          </div>
        </button>
      </div>
    </div>
  );
}
